import { useEffect, useState } from 'react';
import { businessApi } from '@/lib/api-paths';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MAILBOX_ADDON, PLANS } from '@pageboss/shared';
import { useMe } from '@/hooks/use-me';
import { locationRoutes } from '@/lib/route-map';
import { UpgradeGate } from '@/components/upgrade-gate';

// =====================================================================
// MailboxPanel — workspace mailbox management (Phase 6c).
//
// Renders under the domain card on the site-domain page, but ONLY when the
// site has an ACTIVE custom domain (mailboxes attach to a live domain). It:
//   1. finds the active domain via GET /api/domains?locationId=
//   2. lists mailboxes via GET /api/mailboxes?domainId=
//   3. lets the customer add (POST, $4.90/mo) and remove (DELETE) mailboxes
//   4. shows MX/SPF/DKIM records while a mailbox is `provisioning`
// Disabled-safe: when nothing is wired the panel simply renders nothing.
// =====================================================================

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  priority?: number;
}

interface MailboxRow {
  id: string;
  mailboxEmail: string;
  status: 'provisioning' | 'awaiting_password' | 'active' | 'suspended' | 'deleted';
}

interface DomainRow {
  id: string;
  domainName: string;
  status: 'pending_verification' | 'active' | 'expired' | 'deleted';
}

const PRICE_LABEL = `$${(MAILBOX_ADDON.priceCents / 100).toFixed(2)}/mo`;

function errorCopy(code: string): string {
  switch (code) {
    // 'tier_not_allowed' is handled before this function is called — see
    // add()'s gate check (WP1 Task 12).
    case 'email_taken':
      return 'That address is already taken. Try a different name.';
    case 'domain_not_active':
      return 'Your domain needs to finish verifying before you can add a mailbox.';
    case 'billing_failed':
      return "We couldn't set up billing for this mailbox. Please try again.";
    case 'billing_sync_failed':
      return "We couldn't update billing while removing this mailbox. Please try again in a moment.";
    case 'invalid_localpart':
      return 'Use letters, numbers, dots, or hyphens — no spaces or symbols.';
    case 'invalid_input':
      return 'Please enter a name for the mailbox.';
    case 'weak_password':
      return 'Pick a stronger password — at least 8 characters with a letter and a number.';
    case 'not_awaiting_password':
      return 'This mailbox isn’t ready for a password yet. Recheck DNS first.';
    case 'zoho_not_configured':
      return 'Email provisioning isn’t fully set up yet. Please try again shortly.';
    case 'provisioning_failed':
      return 'We couldn’t create the mailbox just now. Please try again in a moment.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

function StatusBadge({ status }: { status: MailboxRow['status'] }) {
  if (status === 'active') return <Badge variant="success">Active</Badge>;
  if (status === 'awaiting_password') return <Badge variant="warning">Set password</Badge>;
  if (status === 'provisioning') return <Badge variant="warning">Verifying DNS</Badge>;
  if (status === 'suspended') return <Badge variant="muted">Suspended</Badge>;
  return <Badge variant="muted">Removed</Badge>;
}

function DnsRow({ record }: { record: DnsRecord }) {
  return (
    <div className="grid grid-cols-[6rem_1fr] gap-x-4 gap-y-1 rounded-md border border-border-subtle bg-card p-3 text-sm">
      <span className="text-text-tertiary">Type</span>
      <code className="font-mono">
        {record.type}
        {record.priority !== undefined ? ` (priority ${record.priority})` : ''}
      </code>
      <span className="text-text-tertiary">Name</span>
      <code className="break-all font-mono">{record.name}</code>
      <span className="text-text-tertiary">Value</span>
      <code className="break-all font-mono">{record.value}</code>
    </div>
  );
}

export function MailboxPanel({ siteId }: { siteId: string }) {
  // Canonical tenant URLs carry the Business id; these screens are only
  // ever rendered inside one, so it comes from the router, not a prop.
  const businessId = useBusinessId() ?? '';
  const meState = useMe();
  const tier = meState.kind === 'ready' ? meState.me.usage.tier : 'free';
  const freeIncluded = tier === 'free' ? 0 : PLANS[tier].freeMailboxesIncluded;
  const [domain, setDomain] = useState<DomainRow | null>(null);
  const [mailboxes, setMailboxes] = useState<MailboxRow[]>([]);
  const [dns, setDns] = useState<DnsRecord[]>([]);
  const [localPart, setLocalPart] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [recheckingId, setRecheckingId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [pw, setPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gate, setGate] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function loadDomain(): Promise<DomainRow | null> {
    const res = await fetch(businessApi(businessId, `domains?locationId=${encodeURIComponent(siteId)}`));
    if (res.status === 401) {
      window.location.href = '/login';
      return null;
    }
    if (!res.ok) return null;
    const data = (await res.json()) as { domains?: DomainRow[] };
    return data.domains?.find((d) => d.status === 'active') ?? null;
  }

  async function loadMailboxes(domainId: string) {
    const res = await fetch(businessApi(businessId, `mailboxes?domainId=${encodeURIComponent(domainId)}`));
    if (res.status === 401) {
      window.location.href = '/login';
      return;
    }
    if (!res.ok) return;
    const data = (await res.json()) as { mailboxes?: MailboxRow[]; dns?: DnsRecord[] };
    setMailboxes((data.mailboxes ?? []).filter((m) => m.status !== 'deleted'));
    setDns(data.dns ?? []);
  }

  async function load() {
    const dom = await loadDomain();
    setDomain(dom);
    if (dom) await loadMailboxes(dom.id);
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function add() {
    if (!domain) return;
    const willBeFree = mailboxes.length < freeIncluded;
    const ok = window.confirm(
      willBeFree
        ? `Add ${localPart}@${domain.domainName}? It's included free with your plan (${freeIncluded} included).`
        : `Add ${localPart}@${domain.domainName} for ${PRICE_LABEL}? This is a recurring charge added to your subscription.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    setGate(false);
    try {
      const res = await fetch(businessApi(businessId, 'mailboxes'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ domainId: domain.id, localPart }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        if (data.error === 'tier_not_allowed') {
          setGate(true);
        } else {
          setError(errorCopy(data.error ?? ''));
        }
        return;
      }
      setLocalPart('');
      await loadMailboxes(domain.id);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function recheck(mb: MailboxRow) {
    if (!domain) return;
    setRecheckingId(mb.id);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(businessApi(businessId, `mailboxes/${mb.id}/recheck`), { method: 'POST' });
      const data = (await res.json().catch(() => ({}))) as { error?: string; verified?: boolean };
      if (!res.ok) {
        setError(errorCopy(data.error ?? ''));
        return;
      }
      if (!data.verified) {
        setNotice('DNS not visible yet — it can take a little while to propagate. Try again soon.');
      }
      await loadMailboxes(domain.id);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setRecheckingId(null);
    }
  }

  async function activate(mb: MailboxRow) {
    if (!domain) return;
    if (pw.length < 8) {
      setError(errorCopy('weak_password'));
      return;
    }
    setPwBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(businessApi(businessId, `mailboxes/${mb.id}/activate`), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(errorCopy(data.error ?? ''));
        return;
      }
      setPw('');
      setActivatingId(null);
      setNotice(`${mb.mailboxEmail} is active. Sign in at mail.zoho.com with the password you just set.`);
      await loadMailboxes(domain.id);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setPwBusy(false);
    }
  }

  async function remove(mb: MailboxRow) {
    if (!domain) return;
    const ok = window.confirm(
      `Remove ${mb.mailboxEmail}? This stops the recurring charge and deletes the mailbox.`,
    );
    if (!ok) return;
    setRemovingId(mb.id);
    setError(null);
    try {
      const res = await fetch(businessApi(businessId, `mailboxes/${mb.id}`), { method: 'DELETE' });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setError(errorCopy(data.error ?? ''));
        return;
      }
      await loadMailboxes(domain.id);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setRemovingId(null);
    }
  }

  // Mailboxes need a live domain to attach to. Loading stays silent (no
  // flash of the locked state before the fetch resolves); once resolved,
  // a missing domain gets a visible, honest explainer instead of the panel
  // silently rendering nothing (WP1 Task 7 — interim target per the
  // ledger ruling). WP3b (D4): the explainer's link now opens the guided
  // domain & mailbox wizard (routes.domainWizard) instead of the raw domain
  // panel (routes.websiteDomain) — the wizard derives its own starting step
  // from this same live domain status, so a pending domain lands the owner
  // on the verification-wait step, not back at step 1.
  if (loading) return null;
  if (!domain) {
    return (
      <div className="space-y-2 rounded-lg border border-border-subtle bg-card p-6">
        <h2 className="text-sm font-semibold text-foreground">Mailboxes</h2>
        <p className="text-sm text-text-secondary">
          Mailboxes attach to a domain. Connect a domain first, then come back here to add
          professional email addresses like <code className="font-mono text-xs">info@yourdomain.com</code>.
        </p>
        <Link
          to={locationRoutes.domainWizard(businessId, siteId)}
          className="inline-flex w-fit items-center text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline"
        >
          Connect a domain →
        </Link>
      </div>
    );
  }

  const needsDns = mailboxes.some(
    (m) => m.status === 'provisioning' || m.status === 'awaiting_password' || m.status === 'active',
  );

  return (
    <div className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Mailboxes</h2>
        <p className="mt-1 text-sm text-text-secondary">
          Professional email on <span className="font-medium text-foreground">{domain.domainName}</span> —
          like <code className="font-mono text-xs">info@{domain.domainName}</code>. {PRICE_LABEL} per
          mailbox.
        </p>
      </div>

      {mailboxes.length > 0 ? (
        <ul className="space-y-2">
          {mailboxes.map((mb) => (
            <li
              key={mb.id}
              className="space-y-2 rounded-md border border-border-subtle bg-surface-sunken px-3 py-2 text-sm"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <code className="font-mono text-foreground">{mb.mailboxEmail}</code>
                  <StatusBadge status={mb.status} />
                </span>
                <div className="flex shrink-0 items-center gap-3">
                  {mb.status === 'provisioning' ? (
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => void recheck(mb)}
                      disabled={recheckingId === mb.id}
                      className="h-auto p-0 text-foreground"
                    >
                      {recheckingId === mb.id ? 'Checking…' : 'Recheck DNS'}
                    </Button>
                  ) : null}
                  {mb.status === 'awaiting_password' && activatingId !== mb.id ? (
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => {
                        setActivatingId(mb.id);
                        setPw('');
                        setError(null);
                      }}
                      className="h-auto p-0 text-foreground"
                    >
                      Set password
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => void remove(mb)}
                    disabled={removingId === mb.id}
                    className="h-auto p-0 text-danger-fg"
                  >
                    {removingId === mb.id ? 'Removing…' : 'Remove'}
                  </Button>
                </div>
              </div>

              {mb.status === 'awaiting_password' && activatingId === mb.id ? (
                <div className="space-y-2 rounded-md bg-background p-3">
                  <p className="text-sm text-text-secondary">
                    Your domain is verified. Choose a password to activate{' '}
                    <code className="font-mono text-xs">{mb.mailboxEmail}</code>. We never store it —
                    you’ll use it to sign in at mail.zoho.com.
                  </p>
                  {/* B2: the placeholder was doing the label's job — it
                      disappears the moment you type, and the rules it carried
                      ("8+ chars, a letter & a number") disappeared with it.
                      Visible label above, rules as persistent help below. */}
                  <Label htmlFor={`mailbox-password-${mb.id}`}>Password</Label>
                  <div className="flex items-stretch gap-2">
                    <Input
                      id={`mailbox-password-${mb.id}`}
                      type="password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      aria-describedby={`mailbox-password-help-${mb.id}`}
                      autoComplete="new-password"
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && pw.length >= 8 && !pwBusy) void activate(mb);
                      }}
                    />
                    <Button
                      type="button"
                      disabled={pwBusy || pw.length < 8}
                      onClick={() => void activate(mb)}
                    >
                      {pwBusy ? 'Activating…' : 'Activate'}
                    </Button>
                  </div>
                  <p
                    id={`mailbox-password-help-${mb.id}`}
                    className="text-xs text-text-secondary"
                  >
                    At least 8 characters, with a letter and a number.
                  </p>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {/* T11/A8: at 375 px this row was one flex line — a full-width input,
          an unshrinkable `@domain` suffix and a "Add · $4.90/mo" button —
          so a long domain pushed the button off the card. It stacks below the
          `sm` breakpoint: input row first, button full width underneath. The
          suffix is a shrinkable adornment (`min-w-0 truncate`) so the longest
          domain narrows itself instead of shoving the field. */}
      {/* B2: `info` as a placeholder read as a filled value, not as a label —
          nothing on screen said what the field was for. */}
      <Label htmlFor="mailbox-local-part">New mailbox address</Label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <div className="flex w-full min-w-0 items-center rounded-md border border-input bg-background pr-3 focus-within:ring-2 focus-within:ring-ring sm:flex-1">
          <Input
            id="mailbox-local-part"
            value={localPart}
            onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
            placeholder="info"
            className="min-w-0 flex-1 border-0 focus-visible:ring-0"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && localPart.trim().length > 0 && !busy) {
                void add();
              }
            }}
          />
          <span className="min-w-0 truncate font-mono text-sm text-text-tertiary">
            @{domain.domainName}
          </span>
        </div>
        {/* B4: a filled button that cannot be pressed reads as the screen's
            primary action while doing nothing. Outline until there is
            something to add; while it IS adding, the fill stays (T8 ruling:
            never change variant mid-action). */}
        <Button
          type="button"
          variant={localPart.trim().length === 0 ? 'outline' : 'default'}
          className="w-full sm:w-auto"
          disabled={busy || localPart.trim().length === 0}
          onClick={() => void add()}
        >
          {busy ? 'Adding…' : `Add · ${PRICE_LABEL}`}
        </Button>
      </div>

      {gate ? (
        <UpgradeGate capability="Mailboxes need a paid plan." planKey="pro" from={window.location.pathname} />
      ) : error ? (
        <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{error}</p>
      ) : null}

      {notice ? (
        <p className="rounded-md bg-surface-sunken px-3 py-2 text-sm text-text-secondary">{notice}</p>
      ) : null}

      {needsDns && dns.length > 0 ? (
        <div className="space-y-3 rounded-lg border border-warning-bg bg-warning-bg/40 p-4">
          <p className="text-sm text-text-secondary">
            Add these records at your domain's DNS provider to receive mail.
          </p>
          <div className="space-y-2">
            {dns.map((r) => (
              <DnsRow key={`${r.type}-${r.name}-${r.value}`} record={r} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
